// i18n (KR/EN/JP) — 바닐라 dcf.js 의 I18N/SELECTS_I18N/PH_TEXT/unitMoney·unitShare 를
// React 식으로 이식. 바닐라는 kr 을 DOM 원문(data-kr)에서 읽었지만, React 는 kr 사전을
// 신설해 3개 언어를 대칭 테이블로 관리한다 — 키 누락이 구조적으로 불가능하도록
// [kr, en, jp] 튜플 1개 테이블로 선언한다.
//
// 언어 선택 저장 키는 바닐라와 동일한 `dcf_lang` — /dcf(바닐라)와 /v2/dcf 가 공유.
import { useMemo } from "react";
import { create } from "zustand";

export type Lang = "kr" | "en" | "jp";
export const LANGS: readonly Lang[] = ["kr", "en", "jp"] as const;

type Tri = readonly [kr: string, en: string, jp: string];

/** 번역 테이블 — 키: 'nav.*' 내비게이션, 'dash.*' 대시보드, 'new.*' 새 프로젝트,
 *  'tab.*' 워크스페이스 탭, 'btn.*' 버튼, 'h.*' 카드 제목, 'lbl.*' 폼 라벨,
 *  'opt.*' select 옵션, 'col.*' 표 헤더, 'row.*'/'out.*'/'tv.*'/'br.*'/'wr.*' 표 행,
 *  'ph.*' placeholder, 'hint.*' 힌트, 'chip.*' 채팅 퀵칩, 'chat.*' 채팅, 'unit.*' 단위 */
export const I18N_TABLE: Record<string, Tri> = {
  // ── 내비게이션 ──
  "nav.back": ["← 메인 에이전트", "← Main Agent", "← メインへ"],
  "nav.home": ["Home", "Home", "Home"],
  "nav.projects": ["Projects", "Projects", "Projects"],
  "lbl.contact_email": ["평가문의 : ", "Inquiries: ", "お問い合わせ: "],
  "home.h1a": ["프로젝트로 관리하는", "Project-managed", "プロジェクトで管理する"],
  "home.p": [
    "회사별 프로젝트를 만들고 가정·추정을 입력하면, DeepSearch 밸류에이션 엔진이 FCFF·WACC·Terminal Value·민감도까지 계산하고 평가보고서 문구와 Excel을 생성합니다.",
    "Create a project per company and enter assumptions — the DeepSearch valuation engine computes FCFF, WACC, terminal value and sensitivities, then generates report language and Excel files.",
    "会社ごとにプロジェクトを作成し、前提・予測を入力すると、DeepSearchバリュエーションエンジンがFCFF・WACC・ターミナルバリュー・感応度まで計算し、評価報告書の文言とExcelを生成します。",
  ],
  "home.btn_new": ["새 DCF 프로젝트", "New DCF Project", "新規DCFプロジェクト"],
  "home.btn_projects": ["내 프로젝트", "My Projects", "マイプロジェクト"],
  "home.feat_title": ["대화형 입력과 실제 평가 엔진의 결합", "Conversational input meets a real valuation engine", "対話型入力と実評価エンジンの融合"],
  "home.feat_sub": [
    "UI 입력은 그대로 DeepSearch 밸류에이션 백엔드로 전달되어 정식 결과를 산출합니다.",
    "Your inputs go straight to the DeepSearch valuation backend to produce official results.",
    "UIの入力はそのままDeepSearchバリュエーションバックエンドへ渡され、正式な結果を算出します。",
  ],
  "home.f1t": ["프로젝트 관리", "Project Management", "プロジェクト管理"],
  "home.f1d": [
    "회사별로 가정·결과를 저장하고 다시 열어 이어서 작업합니다.",
    "Save assumptions and results per company and pick up where you left off.",
    "会社ごとに前提・結果を保存し、続きから作業できます。",
  ],
  "home.f2t": ["DCF 평가", "DCF Valuation", "DCF評価"],
  "home.f2d": [
    "유사기업 선정부터 WACC·FCFF까지 가정을 입력해 간단하게 평가합니다.",
    "From peer selection to WACC and FCFF — value companies with simple inputs.",
    "類似企業の選定からWACC・FCFFまで、前提を入力して簡単に評価します。",
  ],
  "home.f3t": ["민감도 분석", "Sensitivity Analysis", "感応度分析"],
  "home.f3d": [
    "WACC×영구성장률 매트릭스를 자동 생성합니다.",
    "Auto-generates a WACC × terminal-growth matrix.",
    "WACC×永久成長率マトリクスを自動生成します。",
  ],
  "home.f4t": ["보고서·Excel", "Report & Excel", "報告書・Excel"],
  "home.f4d": [
    "평가보고서 문구와 Excel 파일을 즉시 생성합니다.",
    "Instantly generates report language and Excel files.",
    "評価報告書の文言とExcelファイルを即時生成します。",
  ],
  "home.steps_title": ["DCF Valuation 프로세스", "DCF Valuation Process", "DCF Valuationプロセス"],
  "home.steps_sub": [
    "6단계 구조화 흐름으로 누락 없이 완성합니다.",
    "A structured six-step flow, nothing missed.",
    "6段階の構造化フローで漏れなく完成します。",
  ],
  "home.s1": ["프로젝트 생성", "Create project", "プロジェクト作成"],
  "home.s2": ["가정 입력", "Enter assumptions", "前提入力"],
  "home.s3": ["추정 드라이버", "Forecast drivers", "予測ドライバー"],
  "home.s4": ["엔진 계산", "Engine calculation", "エンジン計算"],
  "home.s5": ["민감도 검토", "Review sensitivity", "感応度レビュー"],
  "home.s6": ["보고서·Excel", "Report & Excel", "報告書・Excel"],
  "home.mn": ["백만", "M KRW", "百万"],
  "home.won": ["원", "KRW", "円"],
  "nav.logout": ["로그아웃", "Log out", "ログアウト"],
  "nav.user_fallback": ["사용자", "User", "ユーザー"],

  // ── 대시보드 ──
  "dash.title": ["DCF 프로젝트", "DCF Projects", "DCFプロジェクト"],
  "dash.empty": [
    "프로젝트가 없습니다. + New Valuation 으로 시작하세요.",
    "No projects yet. Start with + New Valuation.",
    "プロジェクトがありません。+ New Valuation で開始してください。",
  ],
  "dash.open": ["열기 →", "Open →", "開く →"],
  "dash.del": ["삭제", "Delete", "削除"],
  "col.company": ["회사명", "Company", "会社名"],
  "col.val_date": ["평가기준일", "Valuation Date", "評価基準日"],
  "col.status": ["진행상태", "Status", "ステータス"],
  "col.pps": ["주당가치", "Price / Share", "株価"],
  "col.updated": ["최근 수정", "Updated", "最終更新"],
  "status.done": ["계산 완료", "Calculated", "計算完了"],
  "status.wip": ["작성 중", "In Progress", "作成中"],

  // ── 새 프로젝트 ──
  "new.title": ["새 DCF Valuation 프로젝트", "New DCF Valuation Project", "新規DCF Valuationプロジェクト"],
  "new.sub": [
    "기본 정보를 입력하면 샘플 가정으로 프로젝트가 생성되고, Workspace에서 값을 수정·재계산할 수 있습니다.",
    "Enter basic info to create a project with sample assumptions, then edit and recalculate in the Workspace.",
    "基本情報を入力するとサンプル前提でプロジェクトが作成され、Workspaceで値を修正・再計算できます。",
  ],
  "new.basic": ["기본 정보", "Basic Info", "基本情報"],
  "new.company_ph": ["예) ABC바이오", "e.g. ABC Bio", "例) ABCバイオ"],
  "new.start_data": ["시작 데이터", "Starting Data", "開始データ"],
  "new.sample": ["샘플 가정으로 시작", "Start with Sample", "サンプル前提で開始"],
  "new.sample_desc": [
    "백엔드 샘플 페이로드로 즉시 시작",
    "Start instantly with a sample payload",
    "サンプルペイロードで即時開始",
  ],
  "new.blank": ["빈 양식", "Blank Form", "空フォーム"],
  "new.blank_desc": ["직접 입력 (매출·비용·CAPEX)", "Manual input (revenue · cost · CAPEX)", "手入力 (売上·費用·CAPEX)"],
  "new.soon": ["다음 단계 지원", "Coming soon", "次段階で対応"],
  "new.upload": ["파일 업로드", "File Upload", "ファイルアップロード"],
  "new.upload_desc": [
    "재무제표·사업계획 파일에서 자동 추출",
    "Auto-extract from financial statements / business plans",
    "財務諸表·事業計画から自動抽出",
  ],
  "new.file_select": ["파일 선택", "Choose Files", "ファイル選択"],
  "new.template": ["예시 템플릿(샘플숫자기재)", "Template (with sample numbers)", "テンプレート（サンプル数値入り）"],
  "new.template_blank": ["예시 템플릿(빈양식)", "Template (blank form)", "テンプレート（空欄）"],
  "new.file_hint": [
    "재무제표·사업계획서(Excel/PDF/Word/PowerPoint/이미지)를 업로드하면 재무정보를 자동 추출합니다. 여러 파일을 동시에 선택할 수 있습니다.",
    "Upload financial statements or business plans (Excel/PDF/Word/PowerPoint/images) to auto-extract financial data. Multiple files supported.",
    "財務諸表・事業計画書(Excel/PDF/Word/PowerPoint/画像)をアップロードすると財務情報を自動抽出します。複数ファイル選択可。",
  ],
  "new.file_selected": ["선택됨: ", "Selected: ", "選択済み: "],
  "new.cancel": ["취소", "Cancel", "キャンセル"],
  "new.create": ["프로젝트 생성 →", "Create Project →", "プロジェクト作成 →"],
  "opt.m12": ["12월", "Dec (12)", "12月"],
  "opt.m3": ["3월", "Mar (3)", "3月"],
  "opt.m6": ["6월", "Jun (6)", "6月"],
  "opt.m9": ["9월", "Sep (9)", "9月"],

  // ── 워크스페이스 공통 ──
  "tab.summary": ["Summary", "Summary", "サマリー"],
  "tab.assumptions": ["Assumptions", "Assumptions", "前提条件"],
  "tab.projection": ["Projection", "Projection", "予測"],
  "tab.wacc": ["WACC", "WACC", "WACC"],
  "tab.dcf": ["DCF Result", "DCF Result", "DCF結果"],
  "tab.sensitivity": ["Sensitivity", "Sensitivity", "感応度分析"],
  "tab.report": ["Report", "Report", "レポート"],
  "btn.recalc": ["재계산", "Recalculate", "再計算"],
  "btn.save": ["저장", "Save", "保存"],
  "btn.saved": ["저장됨", "Saved", "保存済み"],
  "btn.versions": ["버전", "History", "バージョン"],
  "btn.to_dashboard": ["대시보드로", "To Dashboard", "ダッシュボードへ"],
  "ws.refreshed": ["입력 갱신됨", "Inputs updated", "入力更新済み"],
  "ver.empty": [
    '저장된 버전이 없습니다. "저장"을 누르면 자동으로 버전이 기록됩니다.',
    'No saved versions. Press "Save" to record a version automatically.',
    "保存されたバージョンがありません。「保存」を押すと自動記録されます。",
  ],
  "ver.untitled": ["무제 버전", "Untitled version", "無題バージョン"],
  "ver.restore": ["복원", "Restore", "復元"],

  // ── Summary 탭 ──
  "h.key_assumptions": ["주요 가정", "Key Assumptions", "主要前提"],
  "h.agent_summary": ["Agent 검토 요약", "Agent Review Summary", "エージェント検討要約"],
  "kpi.tv_sub": ["Terminal Value 비중", "Terminal Value share", "Terminal Value比率"],
  "lbl.forecast_period": ["추정기간", "Forecast Period", "予測期間"],
  "lbl.wacc_row": ["WACC (할인율)", "WACC (Discount Rate)", "WACC (割引率)"],
  "lbl.tgr_row": ["영구성장률", "Terminal Growth", "永続成長率"],
  "lbl.tax_row": ["법인세율", "Tax Rate", "法人税率"],
  "lbl.net_debt": ["순차입금", "Net Debt", "純有利子負債"],
  "lbl.shares_row": ["발행주식수", "Shares Outstanding", "発行株式数"],
  "lbl.diluted_row": ["희석 주식수", "Diluted Shares", "希薄化後株数"],
  "lbl.diluted_pps_row": ["희석 후 주당가치", "Diluted PPS", "希薄化後株価"],
  "ph.recalc_auto": ["재계산 후 자동 생성됩니다.", "Auto-generated after recalculation.", "再計算後に自動生成されます。"],
  "ph.recalc_show": ["재계산 후 표시됩니다.", "Displayed after recalculation.", "再計算後に表示されます。"],

  // ── Assumptions 탭 ──
  "h.basic_assumptions": ["기본 가정", "Basic Assumptions", "基本前提"],
  "h.capital_core": ["자본 구조 (핵심)", "Capital Structure (core)", "資本構成 (コア)"],
  "lbl.company": ["회사명", "Company Name", "会社名"],
  "lbl.biz_desc": ["사업내용 (선택)", "Business (optional)", "事業内容 (任意)"],
  "ph.biz_desc": [
    "주요 제품·서비스, 사업 구조 등",
    "Products/services, business model",
    "主要製品・サービスなど",
  ],
  "lbl.val_date": ["평가기준일", "Valuation Date", "評価基準日"],
  "lbl.fye": ["결산월", "FY End Month", "決算月"],
  "lbl.forecast_yrs": ["추정기간 (년)", "Forecast Period (yr)", "予測期間 (年)"],
  "lbl.tax_rate": ["법인세율 (%)", "Tax Rate (%)", "法人税率 (%)"],
  "lbl.wacc": ["WACC / 할인율 (%)", "WACC / Discount Rate (%)", "WACC / 割引率 (%)"],
  "lbl.tgr": ["영구성장률 (%)", "Terminal Growth Rate (%)", "永続成長率 (%)"],
  "hint.wacc_module": [
    "· WACC 모듈 산출 적용 — 직접 수정 시 직접입력으로 전환",
    "· WACC module applied — editing switches to direct input",
    "· WACCモジュール適用 — 編集で直接入力に切替",
  ],
  "lbl.non_op": ["비영업자산", "Non-Op. Assets", "非事業資産"],
  "lbl.debt": ["이자부부채", "Interest-Bearing Debt", "有利子負債"],
  "lbl.shares": ["보통주 발행주식수 (주)", "Common Shares Outstanding", "普通株発行株数 (株)"],
  "grp.pref": ["우선주", "Preferred Stock", "優先株"],
  "grp.so": ["스톡옵션", "Stock Options", "ストックオプション"],
  "lbl.fd_shares_auto": ["완전희석 주식수 (주)", "Fully-diluted Shares", "完全希薄化株数 (株)"],
  "lbl.fd_auto": ["자동 계산", "auto", "自動計算"],
  "fd.common": ["보통주", "Common", "普通株"],
  "fd.pref": ["우선주 전환", "Pref. conversion", "優先株転換"],
  "fd.so": ["스톡옵션 순증(TSM)", "Net options (TSM)", "SO純増(TSM)"],
  "fd.override": ["직접 입력 우선", "override in use", "直接入力優先"],
  "lbl.pref_amount": ["우선주 투자금액/발행금액 (백만원)", "Preferred Stock Amount (M KRW)", "優先株投資額/発行額 (百万W)"],
  "lbl.pref_shares": ["우선주 주식수 (주, 선택)", "Preferred Shares (optional)", "優先株株数 (株, 任意)"],
  "lbl.fd_shares": ["완전희석 주식수 직접입력 (주, 선택)", "Fully-diluted Shares Override (optional)", "完全希薄化株数 直接入力 (任意)"],
  "lbl.so_shares": ["스톡옵션 발행주식수 (주, 선택)", "Stock Option Shares (optional)", "ストックオプション株数 (任意)"],
  "lbl.so_strike": ["스톡옵션 행사가액 (원/주, 선택)", "Option Exercise Price (KRW, optional)", "行使価額 (W/株, 任意)"],
  "lbl.so_ref_price": ["현재 주가·참조가액 (원/주, 선택)", "Reference Share Price (optional)", "参照株価 (任意)"],
  "btn.goto_wacc": ["WACC 탭에서 산출하기", "Open WACC tab", "WACCタブで算出"],
  "row.capex_name": ["CAPEX", "CAPEX", "CAPEX"],
  "lbl.drv_mode": ["입력 단위", "Input unit", "入力単位"],
  "opt.mode_pct": ["% (매출대비)", "% of sales", "% (売上比)"],
  "opt.mode_amt": ["금액 ({m})", "Amount ({m})", "金額 ({m})"],
  "opt.mode_manual": ["직접입력", "Manual entry", "直接入力"],
  "row.inflation": ["물가상승률 (%)", "Inflation (%)", "インフレ率 (%)"],
  "row.wage_growth": ["임금상승률 (%)", "Wage Growth (%)", "賃金上昇率 (%)"],
  "row.market_growth": ["시장성장률 (%)", "Market Growth (%)", "市場成長率 (%)"],
  "btn.save_recalc": ["가정 저장 및 재계산", "Save & Recalculate", "保存して再計算"],
  "hint.assump_more": [
    "과거 손익·추정 드라이버·NWC 는 Projection 탭, WACC 산출은 WACC 탭에서 편집. 나머지 상세 가정(스톡옵션·NOL·우선주 등)은 기존 화면에서 편집하세요 — ",
    "Edit historical P&L / drivers / NWC in the Projection tab and WACC in the WACC tab. Other detailed assumptions (stock options, NOL, preferred) are on the legacy page — ",
    "過去損益・ドライバー・NWCはProjectionタブ、WACCはWACCタブで編集。その他の詳細前提(SO・NOL・優先株など)は既存画面で — ",
  ],
  "hint.assump_more_link": ["/dcf 열기", "Open /dcf", "/dcf を開く"],

  // ── Projection 탭 ──
  "h.historical_pl": ["과거 재무 추이 (Historical P&L)", "Historical P&L", "過去財務 (Historical P&L)"],
  "hint.hist_unit": [
    "단위: {m} · 추정 드라이버의 기준",
    "Unit: {m} · driver basis",
    "単位: {m} · ドライバー基準",
  ],
  "col.year_end": ["연도(YYYY-MM-DD)", "Year End (YYYY-MM-DD)", "年度(YYYY-MM-DD)"],
  "col.revenue": ["매출액", "Revenue", "売上高"],
  "col.cogs": ["매출원가", "COGS", "売上原価"],
  "col.sga": ["판관비", "SG&A", "販管費"],
  "col.dep": ["감가상각", "Depreciation", "減価償却"],
  "col.gross": ["매출총이익", "Gross Profit", "売上総利益"],
  "col.op": ["영업이익", "Operating Profit", "営業利益"],
  "tip.dep": [
    "유형·무형·사용권자산 상각비의 합계를 넣습니다. 판관비에 이미 포함된 금액이라 영업이익 계산에서 다시 빼지 않습니다.",
    "Sum of depreciation on tangible, intangible and right-of-use assets. Already included in SG&A, so it is not deducted again for operating profit.",
    "有形・無形・使用権資産の償却費合計。販管費に含まれるため営業利益からは再控除しません。",
  ],
  "hist.row_n": ["{n}년", "Y{n}", "{n}年"],
  "hist.empty": [
    "「과거 연도 추가」 버튼으로 과거 손익을 입력하세요.",
    "Add historical years with the [+ Add Year] button.",
    "「年度追加」ボタンで過去損益を入力してください。",
  ],
  "btn.add_hist": ["+ 과거 연도 추가", "+ Add Year", "+ 年度追加"],
  "btn.del_row": ["행 삭제", "Delete row", "行削除"],
  "h.forecast_assumptions": ["추정 가정", "Forecast Assumptions", "予測前提"],
  "lbl.scenario": ["시나리오", "Scenario", "シナリオ"],
  "opt.scen_bp": ["사업계획 (Business Plan)", "Business Plan", "事業計画 (Business Plan)"],
  "opt.scen_hist": ["과거 추세 (Historical)", "Historical Trend", "過去推移 (Historical)"],
  "opt.scen_mkt": ["시장성장률 (Market)", "Market Growth", "市場成長率 (Market)"],
  "lbl.rev_mode": ["매출 입력 방식", "Revenue Input Method", "売上入力方式"],
  "opt.rev_amounts": ["사업계획 절대금액 ({m})", "Absolute Amounts ({m})", "絶対金額 ({m})"],
  "opt.rev_growth": ["성장률 (%)", "Growth Rate (%)", "成長率 (%)"],
  "h.driver_input": ["추정 드라이버 입력", "Driver Inputs", "ドライバー入力"],
  "hint.driver_unit": [
    "(매출·CAPEX는 {m}, 비율은 %)",
    "(Revenue·CAPEX in {m}, ratios in %)",
    "(売上·CAPEXは{m}、比率は%)",
  ],
  "col.item": ["구분", "Item", "区分"],
  "row.rev_amount": ["매출액 ({m})", "Revenue ({m})", "売上高 ({m})"],
  "row.rev_growth": ["매출성장률 (%)", "Revenue Growth (%)", "売上成長率 (%)"],
  "row.cogs_ratio": ["매출원가율 (%)", "COGS Ratio (%)", "売上原価率 (%)"],
  "row.sga_ratio": ["판관비율 (%)", "SG&A Ratio (%)", "販管費率 (%)"],
  "row.dep_ratio": ["감가상각률 (매출%)", "Depreciation (% of rev.)", "減価償却率 (売上%)"],
  "row.capex": ["CAPEX ({m})", "CAPEX ({m})", "CAPEX ({m})"],
  "btn.driver_recalc": ["드라이버 적용 및 재계산", "Apply & Recalculate", "適用して再計算"],
  "h.dep_capex": ["감가상각·CapEx 고급 입력", "Dep.·CapEx Advanced Input", "減価償却·CapEx詳細入力"],
  "hint.dep_capex": [
    "(선택 · 비우면 위 기본 방식 사용)",
    "(optional · defaults to the basic method above)",
    "(任意 · 空欄なら上の基本方式)",
  ],
  "lbl.dep_method": ["감가·CapEx 방식", "Dep.·CapEx Method", "減価償却·CapEx方式"],
  "lbl.capex_method": ["CAPEX 추정 방식", "CAPEX Method", "CAPEX推定方式"],
  "opt.capex_reinvest": ["기존 감가상각비 재투자", "Reinvest existing depreciation", "既存減価償却費の再投資"],
  "opt.capex_manual": ["직접입력", "Direct amount", "直接入力"],
  "hint.capex_reinvest": [
    "감가상각비와 같은 금액을 CAPEX 로 본다 — 현 설비를 유지만 하는 가정.",
    "CAPEX equals depreciation — maintenance-only assumption.",
    "減価償却費と同額をCAPEXとみなす — 設備維持のみの仮定。",
  ],
  "hint.fa_unit": ["금액 단위: 백만원", "Amounts in millions (KRW)", "金額単位: 百万ウォン"],
  "opt.dep_ratio": ["감가상각비(매출비율) / CAPEX(직접입력금액)", "Dep. (% of sales) / CAPEX (direct amount)", "減価償却費(売上比率) / CAPEX(直接入力金額)"],
  "opt.dep_register": ["(유무형)자산명세서 (정액상각·재투자)", "Asset Schedule (SL + Reinvestment)", "(有形·無形)資産明細書 (定額償却·再投資)"],
  "tip.dep_locked": [
    "감가상각 방식이 '(유무형)자산명세서'라 자산별 스케줄에서 계산됩니다. 아래 「감가상각·CapEx 고급 입력」에서 '감가상각비(매출비율)'로 바꾸면 이 항목을 직접 입력할 수 있습니다.",
    "Depreciation is computed from the asset schedule (Asset Register). Switch to 'Ratio to sales' under Depreciation & CAPEX below to enter it here.",
    "減価償却は資産台帳のスケジュールから計算されます。下の「減価償却·CAPEX」で「売上比率」に変更すると、ここで直接入力できます。",
  ],
  "tip.capex_locked": [
    "CAPEX 추정 방식이 '기존 감가상각비 재투자'라 감가상각비와 같은 금액이 쓰입니다. 위 「CAPEX 추정 방식」에서 '직접입력'으로 바꾸면 연도별로 입력할 수 있습니다.",
    "CAPEX equals depreciation under the reinvestment method. Switch CAPEX Method to 'Direct amount' above to enter it per year.",
    "CAPEX推定方式が「既存減価償却費の再投資」のため、減価償却費と同額が使われます。上の「CAPEX推定方式」で「直接入力」に変更すると年度別に入力できます。",
  ],
  "col.fa_name": ["계정명", "Account", "科目名"],
  "col.fa_cat": ["구분", "Type", "区分"],
  "col.fa_cost": ["취득원가", "Gross Cost", "取得原価"],
  "col.fa_accdep": ["감가누계액", "Acc. Dep.", "減価償却累計"],
  "col.fa_remyears": ["잔여내용연수", "Remaining Life", "残存耐用年数"],
  "col.fa_life": ["내용연수", "Useful Life", "耐用年数"],
  "opt.fa_tangible": ["유형자산", "Tangible", "有形資産"],
  "opt.fa_intangible": ["무형자산", "Intangible", "無形資産"],
  "opt.fa_rou": ["사용권자산", "Right-of-Use", "使用権資産"],
  "fa.tangible_short": ["유형", "Tangible", "有形"],
  "fa.intangible_short": ["무형", "Intangible", "無形"],
  "fa.rou_short": ["사용권", "RoU", "使用権"],
  "fa.empty": [
    "「자산 계정 추가」로 유형/무형/사용권 자산을 계정별로 입력하세요.",
    "Add tangible/intangible/right-of-use assets per account with [+ Add Asset].",
    "「資産追加」で有形/無形/使用権資産を科目別に入力してください。",
  ],
  "btn.add_fa": ["+ 자산 계정 추가", "+ Add Asset", "+ 資産追加"],
  "btn.add_capex_item": ["+ 투자 항목 추가", "+ Add CapEx item", "+ 投資項目追加"],
  "h.historical_bs": ["과거 운전자본 (재무상태표)", "Historical Working Capital", "過去運転資本 (財政状態表)"],
  "hint.historical_bs": [
    "가장 최근 연도만 입력해도 됩니다 — 직전 순운전자본은 마지막 연도에서 계산됩니다. 운전자본은 첫해 FCF 에 그대로 반영되므로 비워 두면 0 으로 잡힙니다.",
    "The latest year alone is enough — prior NWC comes from the last row. Working capital flows straight into year-1 FCF; leaving it blank treats it as zero.",
    "直近年度のみの入力で足ります — 直前の正味運転資本は最終年度から計算されます。",
  ],
  "row.bs_ar": ["매출채권", "Accounts Receivable", "売上債権"],
  "row.bs_inv": ["재고자산", "Inventory", "棚卸資産"],
  "row.bs_oca": ["기타 유동자산", "Other Current Assets", "その他流動資産"],
  "row.bs_ap": ["매입채무", "Accounts Payable", "仕入債務"],
  "row.bs_ocl": ["기타 유동부채", "Other Current Liab.", "その他流動負債"],
  "row.bs_nwc": ["순운전자본 (계산)", "Net Working Capital (calc)", "正味運転資本 (計算)"],
  "btn.add_bs": ["+ 연도 추가", "+ Add year", "+ 年度追加"],
  "btn.pull_bs_years": ["과거 손익의 연도 가져오기", "Use years from P&L", "損益の年度を取得"],
  "bs.empty": ["연도를 추가해 입력하세요.", "Add a year to enter values.", "年度を追加してください。"],
  "warn.bs_missing": [
    "운전자본 미입력 — 직전 순운전자본이 0 으로 계산됩니다. 첫해 FCF 가 과대·과소 평가될 수 있습니다.",
    "Working capital not entered — prior NWC is treated as 0, which can distort year-1 FCF.",
    "運転資本が未入力 — 直前の正味運転資本が0として計算されます。",
  ],
  "col.capex_name": ["투자 항목", "Item", "投資項目"],
  "col.capex_life": ["내용연수", "Useful Life", "耐用年数"],
  "hint.capex_items": [
    "항목별로 나눠 입력하면 연도별 합계가 CAPEX 로 반영됩니다. 내용연수는 지금은 계산에 쓰이지 않고 저장만 되며, 재투자 가정에 쓸 예정입니다. 항목을 두지 않으면 위 드라이버 표에서 총액으로 입력합니다.",
    "Enter investments per item; the yearly totals feed CAPEX. Useful life is stored for a future reinvestment assumption and is not used in the current calculation. With no items, enter totals in the driver table above.",
    "項目別に入力すると年度別合計がCAPEXに反映されます。耐用年数は現在の計算には使われず保存のみで、再投資仮定に使う予定です。",
  ],
  "hint.fa_reinvest": [
    "재투자 가정: 매년 기존자산 정액상각 + 발생 감가상각비만큼 재투자(CapEx), 취득연도는 반년상각(½) 후 익년부터 온전 상각.",
    "Reinvestment: straight-line depreciation of existing assets each year + CapEx equal to depreciation; half-year convention in the acquisition year.",
    "再投資前提: 毎年既存資産を定額償却 + 減価償却費相当を再投資(CapEx)、取得年は半年償却(½)。",
  ],
  "h.nwc_method": ["운전자본 (NWC) 추정 방식", "NWC Estimation Method", "運転資本 (NWC) 推定方式"],
  "lbl.nwc_calc": ["NWC 계산 방식", "NWC Calculation", "NWC計算方式"],
  "opt.nwc_days": [
    "회전기일 (매출채권/재고/매입채무 Days)",
    "Turnover Days (AR / Inv / AP)",
    "回転日数 (売掛/在庫/買掛 Days)",
  ],
  "opt.nwc_ratio": ["매출 대비 NWC 비율 (%)", "NWC-to-Revenue Ratio (%)", "NWC対売上比率 (%)"],
  "row.ar_days": ["매출채권 회전기일 (AR Days)", "AR Days", "売掛金回転日数 (AR Days)"],
  "row.inv_days": ["재고 회전기일 (Inventory Days)", "Inventory Days", "在庫回転日数 (Inventory Days)"],
  "row.ap_days": ["매입채무 회전기일 (AP Days)", "AP Days", "買掛金回転日数 (AP Days)"],
  "row.nwc_ratio": ["NWC / 매출 비율 (%)", "NWC / Revenue (%)", "NWC / 売上比率 (%)"],
  "h.forecast_output": ["추정 결과 (엔진 산출)", "Forecast Output (Engine)", "予測結果 (エンジン算出)"],
  "out.ebit": ["EBIT (영업이익)", "EBIT (Operating Income)", "EBIT (営業利益)"],
  "out.tax": ["법인세", "Tax", "法人税"],
  "out.dep_add": ["(+)감가상각비", "(+) Depreciation", "(+)減価償却費"],
  "out.capex": ["(-)CAPEX", "(-) CAPEX", "(-)CAPEX"],
  "out.nwc": ["(-)운전자본증감", "(-) Δ NWC", "(-)運転資本増減"],
  "h.fa_schedule": [
    "자산별 감가상각·CapEx 스케줄",
    "Asset Depreciation · CapEx Schedule",
    "資産別 減価償却·設備投資スケジュール",
  ],
  // 자산 스케줄 셀 계산 근거 — 호버 툴팁 (사용자 요청: capex 가 어떻게 나왔는지)
  "tip.fa_dep": [
    "총 상각 = 기존 {a} + 재투자 {b} + 신규 {c}",
    "Total dep = existing {a} + reinvest {b} + new {c}",
    "総償却 = 既存 {a} + 再投資 {b} + 新規 {c}",
  ],
  "tip.fa_capex": [
    "재투자 CapEx = 기존 상각 {a} + 이전 재투자분 상각 {b}\n(발생 감가상각비를 전액 재투자해 자본을 유지하는 가정 · 취득연도 반년상각)",
    "Reinvest CapEx = existing dep {a} + dep on prior reinvestments {b}\n(full reinvestment of depreciation to maintain capital; half-year convention)",
    "再投資CapEx = 既存償却 {a} + 過年度再投資分償却 {b}\n(発生償却費を全額再投資して資本維持 · 取得年度半年償却)",
  ],
  "btn.days_from_hist": ["과거 재무에서 회전기일 계산", "Days from historicals", "過去財務から回転日数を計算"],
  "hint.days_from_hist": [
    "최근 연도 말잔 기준 — AR=매출채권/매출×365, 재고·매입채무=원가 기준. 채운 뒤 손으로 고칠 수 있습니다.",
    "From latest year-end balances — AR on revenue, Inv/AP on COGS. Editable after fill.",
    "直近年度末残高基準 — 売掛=売上、棚卸・買掛=原価基準。入力後に修正可能。",
  ],
  "hint.fa_schedule": ["((유무형)자산명세서 방식)", "(asset schedule method)", "(資産明細書方式)"],
  "col.asset_name": ["자산명", "Asset", "資産名"],
  "row.total": ["합계", "Total", "合計"],
  "lbl.dep_cost": ["감가상각비", "Depreciation", "減価償却費"],

  // ── WACC 탭 ──
  "h.peer_selection": ["유사기업(Peer) 선정", "Peer Selection", "ピア選定"],
  "hint.peer_basis": ["베타·자본구조 산출 기준", "Basis for beta · capital structure", "ベータ·資本構成の算出基準"],
  "lbl.peer_method": ["선정 방식", "Selection Method", "選定方式"],
  "opt.wacc_value": ["WACC 직접입력 (값)", "Direct WACC Input (value)", "WACC直接入力 (値)"],
  "opt.wacc_direct": ["직접 선정 (종목 입력)", "Manual Selection (tickers)", "直接選定 (銘柄入力)"],
  "opt.wacc_auto": ["자동 선정 (사업내용)", "Auto-Select (business description)", "自動選定 (事業内容)"],
  "lbl.wacc_direct": ["WACC (할인율, %)", "WACC (Discount Rate, %)", "WACC (割引率, %)"],
  "hint.wacc_direct": [
    "이 값이 그대로 할인율로 사용됩니다",
    "Used directly as the discount rate",
    "この値がそのまま割引率になります",
  ],
  "lbl.peer_tickers": ["Peer 종목", "Peer Tickers", "ピア銘柄"],
  "hint.peer_search": ["(회사명 검색 후 선택)", "(search company name, then select)", "(会社名で検索して選択)"],
  "ph.peer_search": ["회사명 입력 (예: 삼성전자)", "Company name (e.g. Samsung Electronics)", "会社名を入力 (例: サムスン電子)"],
  "dd.searching": ["검색 중…", "Searching…", "検索中…"],
  "dd.no_results": ["검색 결과가 없습니다.", "No results.", "検索結果がありません。"],
  "lbl.biz_text": ["사업내용", "Business Description", "事業内容"],
  "ph.biz_text": [
    "평가대상의 사업내용을 10자 이상 입력",
    "Describe the business (10+ chars)",
    "評価対象の事業内容を10文字以上入力",
  ],
  "lbl.topk": ["Peer 수 (top_k)", "No. of Peers (top_k)", "ピア数 (top_k)"],
  "btn.peer_lookup": ["유사기업 조회", "Find Peers", "類似企業を検索"],
  "hint.peer_lookup": [
    "사업내용으로 동종 상장사를 선정하고 근거를 표시합니다 (수십 초 소요)",
    "Selects comparable listed companies from the business description and shows the rationale (takes tens of seconds)",
    "事業内容から同業上場企業を選定し根拠を表示します（数十秒かかります）",
  ],
  "peer.keywords": ["도출 키워드", "Derived Keywords", "抽出キーワード"],
  "peer.selected_done": [
    "유사기업 {n}개 선정 완료 — WACC 산출 시 이 목록이 사용됩니다",
    "{n} peers selected — used for the WACC computation",
    "類似企業{n}社を選定 — WACC算出時にこのリストを使用",
  ],
  "peer.score": ["종합", "Score", "総合"],
  "h.wacc_params": ["WACC 파라미터", "WACC Parameters", "WACCパラメータ"],
  "lbl.rf": ["무위험수익률 (Rf)", "Risk-Free Rate (Rf)", "無リスク金利 (Rf)"],
  "hint.rf_kofia": ["KOFIA 국고채 조회", "KOFIA govt. bond quote", "KOFIA国債照会"],
  "opt.rf_tenor": ["만기 {n}년 (KOFIA)", "{n}yr (KOFIA)", "満期{n}年 (KOFIA)"],
  "opt.custom": ["직접입력", "Direct Input", "直接入力"],
  "lbl.rf_direct": ["Rf 직접입력 (%)", "Rf (manual, %)", "Rf直接入力 (%)"],
  "lbl.kd": ["Kd 신용등급 / 만기", "Kd Credit Rating / Maturity", "Kd信用格付け / 満期"],
  "hint.kd_kofia": ["KOFIA 제공: AAA~BBB-", "KOFIA: AAA~BBB-", "KOFIA提供: AAA~BBB-"],
  "opt.year_n": ["{n}년", "{n}yr", "{n}年"],
  "lbl.kd_direct": ["Kd 직접입력 (%)", "Kd (manual, %)", "Kd直接入力 (%)"],
  "hint.kd_pre_tax": ["세전 차입이자율", "Pre-tax borrowing rate", "税引前借入利率"],
  "lbl.mrp": ["시장위험프리미엄 MRP (%)", "Market Risk Premium (%)", "市場リスクプレミアム (%)"],
  "hint.mrp": [
    "한국공인회계사회 연구결과 7~9%",
    "KICPA research: 7~9%",
    "韓国公認会計士会の研究: 7~9%",
  ],
  "lbl.size_premium": ["Size Premium", "Size Premium", "サイズプレミアム"],
  "lbl.size_direct": ["Size Premium 직접입력 (%)", "Size Premium (manual, %)", "サイズプレミアム直接入力 (%)"],
  "lbl.beta": ["베타 관측주기 / 관측기간(년)", "Beta Frequency / Window (yr)", "ベータ観測周期 / 観測期間 (年)"],
  "lbl.peer_agg": ["Peer 집계", "Peer Aggregation", "ピア集計"],
  "btn.wacc_calc": ["WACC 산출", "Compute WACC", "WACC算出"],
  "btn.wacc_excel": ["WACC 엑셀 다운로드", "Download WACC Excel", "WACC Excelダウンロード"],
  "btn.apply_wacc": ["이 WACC를 할인율로 적용", "Apply as Discount Rate", "割引率として適用"],
  "msg.excel_started": [
    "✓ WACC 엑셀 다운로드를 시작했습니다.",
    "✓ WACC Excel download started.",
    "✓ WACC Excelのダウンロードを開始しました。",
  ],
  "btn.download_again": ["다시 받기", "Download again", "再ダウンロード"],
  "h.wacc_result": ["WACC 산출 결과", "WACC Result", "WACC算出結果"],
  "wr.ke": ["자기자본비용 Ke", "Cost of Equity Ke", "株主資本コスト Ke"],
  "wr.kd_at": ["세후 타인자본비용 Kd", "After-tax Cost of Debt Kd", "税引後負債コスト Kd"],
  "wr.kd_pre": ["세전 Kd", "Pre-tax Kd", "税引前 Kd"],
  "wr.rf": ["무위험수익률 Rf", "Risk-Free Rate Rf", "無リスク金利 Rf"],
  "wr.peer_count": ["Peer 수", "No. of Peers", "ピア数"],
  "wr.agg": ["집계방식", "Aggregation", "集計方式"],
  "btn.wacc_basis_excel": ["베타·WACC 산출근거 엑셀", "Beta·WACC Basis Excel", "ベータ·WACC根拠Excel"],
  "hint.wacc_basis": [
    "Peer 베타 unlever/relever·자본구조 산출 과정 포함",
    "Includes peer beta unlever/relever and capital-structure workings",
    "ピアβのunlever/relever・資本構成の算出過程を含む",
  ],
  "h.selected_peers": ["선정된 Peer", "Selected Peers", "選定ピア"],
  "col.ticker": ["종목", "Ticker", "銘柄"],

  // ── DCF Result 탭 ──
  "h.fcff": ["FCFF 현재가치", "FCFF Present Value", "FCFF現在価値"],
  "row.discount_factor": ["할인계수", "Discount Factor", "割引係数"],
  "row.pv": ["현재가치", "Present Value", "現在価値"],
  "tv.terminal_fcff": ["Terminal Year FCFF", "Terminal Year FCFF", "Terminal Year FCFF"],
  "tv.g": ["영구성장률 (g)", "Terminal Growth (g)", "永続成長率 (g)"],
  "tv.pv": ["TV 현재가치", "PV of TV", "TV現在価値"],
  "br.sum_pv": ["명시적 추정기간 FCFF PV", "Explicit-Period FCFF PV", "明示予測期間FCFF PV"],
  "br.tv_pv": ["Terminal Value PV", "Terminal Value PV", "Terminal Value PV"],
  "br.tax_shield": ["법인세 절감 PV", "Tax Shield PV", "税節減PV"],
  "br.non_op": ["비영업자산", "Non-Op. Assets", "非事業資産"],
  "br.debt": ["이자부부채", "Interest-Bearing Debt", "有利子負債"],
  "br.pref": ["우선주 투자금액", "Preferred Stock", "優先株投資額"],
  "br.equity": ["Equity Value (희석 전)", "Equity Value (pre-dilution)", "Equity Value (希薄化前)"],
  "br.shares": ["보통주 발행주식수", "Common Shares Outstanding", "普通株発行株数"],
  "br.pps": ["주당가치 — 희석 전 (Price per Share)", "Price per Share (pre-dilution)", "株価 — 希薄化前 (Price per Share)"],
  "br.dil_shares": ["희석 주식수 (우선주+스톡옵션 전환)", "Diluted Shares (pref. + SO)", "希薄化後株数 (優先株+SO転換)"],
  "br.dil_pps": ["주당가치 — 희석 후 (Diluted PPS)", "Diluted Price per Share", "株価 — 希薄化後 (Diluted PPS)"],

  // ── Sensitivity 탭 ──
  "h.sensitivity": [
    "WACC × 영구성장률 민감도 (주당가치)",
    "WACC × Terminal Growth Rate Sensitivity (Price per Share)",
    "WACC × 永続成長率 感応度 (株価)",
  ],
  "lbl.sens_interp": ["민감도 해석", "Sensitivity Interpretation", "感応度解釈"],

  // ── Report 탭 ──
  "h.ppt_theme": ["PPT 테마", "PPT Theme", "PPTテーマ"],
  "hint.ppt_theme": ["(선택 · 미지정 시 기본 디자인)", "(optional · default design if unset)", "(任意 · 未指定なら基本デザイン)"],
  // 커스텀 색상/폰트·로고 (바닐라 dcf.js EN/JP 사전과 동일 문구)
  "lbl.ppt_custom": [
    "색상·폰트도 직접 커스터마이징",
    "Also customize color · font",
    "色·フォントもカスタマイズ",
  ],
  "lbl.ppt_navy": ["주 색상", "Primary Color", "メインカラー"],
  "lbl.ppt_accent": ["강조 색상", "Accent Color", "アクセントカラー"],
  "lbl.ppt_font": ["폰트", "Font", "フォント"],
  "lbl.ppt_logo_text": ["로고 문구", "Logo Text", "ロゴ文言"],
  "lbl.ppt_logo_img": ["로고 이미지", "Logo Image", "ロゴ画像"],
  "hint.ppt_logo_fmt": [
    "(선택 · PNG/JPG/SVG)",
    "(optional · PNG/JPG/SVG)",
    "(任意 · PNG/JPG/SVG)",
  ],
  "hint.ppt_logo_vector": [
    "AI/EPS/PDF 같은 벡터 CI 파일은 브라우저가 직접 못 읽습니다 — 디자인 툴에서 PNG 또는 SVG로 내보낸 뒤 업로드해주세요.",
    "Browsers cannot read vector CI files such as AI/EPS/PDF — export to PNG or SVG in your design tool and upload again.",
    "AI/EPS/PDFのようなベクターCIファイルはブラウザで直接読めません — デザインツールでPNGまたはSVGに書き出してアップロードしてください。",
  ],
  "lbl.ppt_logo_preview": [
    "표지 슬라이드 배치 기준 미리보기 (실제 크기 비율)",
    "Preview at cover-slide placement (actual size ratio)",
    "表紙スライド配置基準のプレビュー (実寸比率)",
  ],
  "hint.ppt_logo_always": [
    "로고 문구/이미지는 프리셋과 별도로 항상 적용됩니다 — 아래 체크 없이도 반영됩니다.",
    "Logo text/image is always applied regardless of the preset — no need to check the box below.",
    "ロゴ文言/画像はプリセットとは別に常に適用されます — 下のチェックなしでも反映されます。",
  ],
  "ppt.navy_classic": ["네이비 클래식", "Navy Classic", "ネイビークラシック"],
  "ppt.corporate_blue": ["코퍼레이트 블루", "Corporate Blue", "コーポレートブルー"],
  "ppt.forest_green": ["포레스트 그린", "Forest Green", "フォレストグリーン"],
  "ppt.amber_orange": ["앰버 오렌지", "Amber Orange", "アンバーオレンジ"],
  "ppt.monochrome": ["블랙 앤 화이트", "Black & White", "ブラック&ホワイト"],
  "h.output_files": ["산출물 생성", "Generate Output", "成果物生成"],
  "btn.gen_excel": ["Excel 생성", "Generate Excel", "Excel生成"],
  "btn.gen_ppt": ["PPT 생성", "Generate PPT", "PPT生成"],
  "btn.gen_pdf": ["PDF 생성", "Generate PDF", "PDF生成"],
  "msg.download_started": [
    "✓ {label} 다운로드를 시작했습니다.",
    "✓ {label} download started.",
    "✓ {label} のダウンロードを開始しました。",
  ],
  "btn.copy": ["클립보드 복사", "Copy to Clipboard", "クリップボードにコピー"],
  "btn.copied": ["복사됨", "Copied", "コピー済み"],
  "h.dcf_summary": ["DCF 평가결과 요약", "DCF Result Summary", "DCF評価結果要約"],
  "lbl.method": ["평가방법", "Method", "評価方法"],

  // ── 채팅 패널 ──
  "lbl.online": ["온라인", "Online", "オンライン"],
  "chat.collapse": ["채팅 접기", "Collapse chat", "チャットを閉じる"],
  "chat.open": ["채팅 열기", "Open chat", "チャットを開く"],
  "chat.placeholder": [
    "DCF 가정이나 결과에 대해 물어보세요",
    "Ask about DCF assumptions or results",
    "DCFの前提や結果について質問してください",
  ],
  "chat.send": ["메시지 보내기", "Send message", "メッセージ送信"],
  "chat.resizer": [
    "드래그해서 너비 조절 (더블클릭: 기본값)",
    "Drag to resize (double-click: default)",
    "ドラッグで幅調整 (ダブルクリック: 既定値)",
  ],
  "chip.dcf_explain": ["DCF 평가 설명", "Explain DCF", "DCF説明"],
  "chip.review": ["검토 코멘트", "Review Comments", "レビューコメント"],
  "chip.review_ready": ["리뷰 준비", "Review Prep", "レビュー準備"],
  "chip.revenue": ["매출 시나리오", "Revenue Scenario", "売上シナリオ"],
  "chip.cogs": ["매출원가", "COGS", "売上原価"],
  "chip.sga": ["판관비", "SG&A", "販管費"],
  "chip.capex": ["CAPEX", "CAPEX", "CAPEX"],
  "chip.macro": ["물가·거시", "Inflation·Macro", "インフレ·マクロ"],
  "chip.wacc_tv": ["WACC·TV", "WACC·TV", "WACC·TV"],

  // ── 단위 ──
  "unit.money": ["백만원", "₩M", "百万円"],
  // 결과 섹션 제목 옆 단위 — 주당가치만 원 단위라, 나머지가 백만원임을 매 섹션에서 밝힌다
  "hint.unit_m": ["단위: {m}", "Unit: {m}", "単位: {m}"],
  "btn.undo_del": ["삭제 취소 — {name}", "Undo delete — {name}", "削除を元に戻す — {name}"],
  "unit.share_ccy": ["원", "₩", "円"],
  "unit.won_suffix": ["원", " KRW", "円"],
  "unit.shares_suffix": ["주", " sh", "株"],
  "unit.years_suffix": ["개년", " yrs", "ヶ年"],
};

const LANG_IDX: Record<Lang, 0 | 1 | 2> = { kr: 0, en: 1, jp: 2 };

/** 순수 번역 함수 — 미등록 키는 키 자체를 반환(개발 중 누락 발견용) */
export function tr(lang: Lang, key: string): string {
  const e = I18N_TABLE[key];
  if (!e) return key;
  return e[LANG_IDX[lang]];
}

/** {n}·{m}·{label} 등 플레이스홀더 치환 */
export function trFmt(lang: Lang, key: string, vars: Record<string, string | number>): string {
  let s = tr(lang, key);
  for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** 금액 단위 (바닐라 unitMoney) */
export function unitMoney(lang: Lang): string {
  return tr(lang, "unit.money");
}

/** 주당가치 통화 단위 (바닐라 unitShare) */
export function unitShare(lang: Lang): string {
  return tr(lang, "unit.share_ccy");
}

/** 표 헤더 'N년차' 표기 */
export function yearCol(lang: Lang, n: number): string {
  return lang === "jp" ? `${n}年目` : lang === "en" ? `Y${n}` : `${n}년차`;
}

// ── zustand 스토어 — 바닐라와 동일한 localStorage 키(dcf_lang)로 유지 ──
const LANG_KEY = "dcf_lang";

function initLang(): Lang {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "en" || v === "jp" || v === "kr") return v;
  } catch {
    /* SSR/테스트 등 localStorage 없음 */
  }
  return "kr";
}

interface I18nState {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const useI18n = create<I18nState>((set) => ({
  lang: initLang(),
  setLang: (l) => {
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      /* noop */
    }
    set({ lang: l });
  },
}));

/** 컴포넌트용 t(key) — 언어 변경 시 자동 리렌더 */
export function useT() {
  const lang = useI18n((s) => s.lang);
  return useMemo(() => {
    const t = (key: string) => tr(lang, key);
    const tf = (key: string, vars: Record<string, string | number>) => trFmt(lang, key, vars);
    return { lang, t, tf };
  }, [lang]);
}
